import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";

const ViewDatesheet = () => {
  const { id } = useParams();
  const location = useLocation();
  const examFromState = location.state?.exam;

  const [examData, setExamData] = useState(
    examFromState || null
  );

  const [isEditing, setIsEditing] = useState(false);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Co-Scholastic Area input
  const [newCoScholasticArea, setNewCoScholasticArea] =
    useState("");

  const navigate = useNavigate();

  // ======================================================
  // Helper to display date as MM/DD/YYYY
  // ======================================================

  const formatDateMMDDYYYY = (date) => {
    if (!date) return "";

    const d = new Date(date);

    if (isNaN(d.getTime())) return "";

    const month = String(
      d.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      d.getDate()
    ).padStart(2, "0");

    const year = d.getFullYear();

    return `${month}/${day}/${year}`;
  };

  // ======================================================
  // Fetch exam, all class subjects and teachers
  // ======================================================

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const sessionId =
          localStorage.getItem(
            "currentSessionId"
          );

        if (!sessionId) {
          alert(
            "Session ID is missing. Please log in again."
          );
          return;
        }

        // ==================================================
        // FETCH EXAM
        // ==================================================

        const examResponse =
          await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/exams/getExam/${id}`,
            {
              params: {
                sessionId,
              },
            }
          );

        console.log(
          "📦 Exam response from backend:",
          examResponse.data
        );

        console.log(
          "📚 Existing Datesheet Subjects:",
          examResponse.data.subjects
        );

        // ==================================================
        // FETCH ALL SUBJECTS OF THIS CLASS
        // ==================================================

        let allClassSubjects = [];

        try {
          const subjectResponse =
            await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${examResponse.data.class}`,
              {
                params: {
                  sessionId,
                },
              }
            );

          console.log(
            "📚 All Class Subjects:",
            subjectResponse.data
          );

          allClassSubjects =
            Array.isArray(
              subjectResponse.data
            )
              ? subjectResponse.data
              : [];
        } catch (subjectError) {
          console.error(
            "Error fetching class subjects:",
            subjectError
          );

          // If subject API fails, keep existing
          // datesheet subjects instead of breaking
          allClassSubjects = [];
        }

        // ==================================================
        // EXISTING DATESHEET SUBJECTS
        // ==================================================

        const existingSubjects =
          examResponse.data.subjects || [];

        const existingSubjectMap =
          new Map();

        existingSubjects.forEach(
          (sub) => {
            existingSubjectMap.set(
              String(
                sub.subjectName || ""
              )
                .trim()
                .toLowerCase(),
              sub
            );
          }
        );

        // ==================================================
        // MERGE ALL CLASS SUBJECTS
        //
        // Existing datesheet data remains intact.
        // Missing subjects get blank fields.
        // ==================================================

        const mergedSubjects =
          allClassSubjects.map(
            (subject) => {
              const key = String(
                subject.subjectName || ""
              )
                .trim()
                .toLowerCase();

              const existing =
                existingSubjectMap.get(
                  key
                );

              if (existing) {
                return {
                  ...existing,

                  examDate:
                    existing.examDate
                      ? new Date(
                          existing.examDate
                        )
                          .toISOString()
                          .split("T")[0]
                      : "",
                };
              }

              return {
                subjectName:
                  subject.subjectName,

                examDate: "",
                startTime: "",
                endTime: "",
                invigilator: "",
                notes: "",
                room: "",
              };
            }
          );

        // ==================================================
        // KEEP OLD DATESHEET SUBJECTS
        // EVEN IF THEY ARE NOT CURRENTLY
        // IN THE CLASS SUBJECT LIST
        // ==================================================

        existingSubjects.forEach(
          (existing) => {
            const key = String(
              existing.subjectName || ""
            )
              .trim()
              .toLowerCase();

            const alreadyIncluded =
              mergedSubjects.some(
                (subject) =>
                  String(
                    subject.subjectName ||
                      ""
                  )
                    .trim()
                    .toLowerCase() ===
                  key
              );

            if (!alreadyIncluded) {
              mergedSubjects.push({
                ...existing,

                examDate:
                  existing.examDate
                    ? new Date(
                        existing.examDate
                      )
                        .toISOString()
                        .split("T")[0]
                    : "",
              });
            }
          }
        );

        // ==================================================
        // SET EXAM DATA
        // ==================================================

        setExamData({
          ...examResponse.data,

          subjects: mergedSubjects,

          coScholasticAreas:
            examResponse.data
              .coScholasticAreas || [],
        });

        // ==================================================
        // FETCH TEACHERS
        // ==================================================

        const teacherResponse =
          await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeachers`,
            {
              params: {
                sessionId,
              },
            }
          );

        console.log(
          "👨‍🏫 Teachers response:",
          teacherResponse.data
        );

        const teachersArray =
          Array.isArray(
            teacherResponse.data
          )
            ? teacherResponse.data
            : [teacherResponse.data];

        const options =
          teachersArray
            .filter(
              (teacher) =>
                teacher?._id
            )
            .map((teacher) => ({
              value: teacher._id,
              label:
                `${teacher.firstName || ""} ${
                  teacher.lastName || ""
                }`.trim(),
            }));

        setTeacherOptions(options);
      } catch (err) {
        console.error(
          "Error loading datesheet data:",
          err
        );

        alert(
          "Failed to load exam data"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // ======================================================
  // Loading
  // ======================================================

  if (loading && !examData) {
    return (
      <h2 className="text-center mt-10 text-gray-500">
        Loading...
      </h2>
    );
  }

  // ======================================================
  // No exam data
  // ======================================================

  if (!examData) {
    return (
      <h2 className="text-center mt-10 text-red-500">
        Exam data not found
      </h2>
    );
  }

  // ======================================================
  // Handle subject field changes
  // ======================================================

  const handleChange = (
    index,
    field,
    value
  ) => {
    const updatedSubjects = [
      ...(examData.subjects || []),
    ];

    updatedSubjects[index] = {
      ...updatedSubjects[index],
      [field]: value,
    };

    setExamData({
      ...examData,
      subjects: updatedSubjects,
    });
  };

  // ======================================================
  // Handle exam name change
  // ======================================================

  const handleExamNameChange = (e) => {
    setExamData({
      ...examData,
      examName: e.target.value,
    });
  };

  // ======================================================
  // Add Co-Scholastic Area
  // ======================================================

  const handleAddCoScholasticArea = () => {
    const areaName =
      newCoScholasticArea.trim();

    if (!areaName) {
      alert(
        "Please enter Co-Scholastic Area name."
      );
      return;
    }

    const existingAreas =
      examData.coScholasticAreas || [];

    const alreadyExists =
      existingAreas.some(
        (area) =>
          String(
            area?.name || ""
          )
            .trim()
            .toLowerCase() ===
          areaName.toLowerCase()
      );

    if (alreadyExists) {
      alert(
        "This Co-Scholastic Area already exists."
      );
      return;
    }

    setExamData({
      ...examData,

      coScholasticAreas: [
        ...existingAreas,
        {
          name: areaName,
        },
      ],
    });

    setNewCoScholasticArea("");
  };

  // ======================================================
  // Delete Co-Scholastic Area
  // ======================================================

  const handleDeleteCoScholasticArea = (
    index
  ) => {
    const existingAreas =
      examData.coScholasticAreas || [];

    const updatedAreas =
      existingAreas.filter(
        (_, areaIndex) =>
          areaIndex !== index
      );

    setExamData({
      ...examData,

      coScholasticAreas:
        updatedAreas,
    });
  };

  // ======================================================
  // Delete subject
  // ======================================================

  const handleDeleteSubject = (
    index
  ) => {
    if (
      examData.subjects.length <= 1
    ) {
      alert(
        "At least one subject is required in the datesheet."
      );
      return;
    }

    const subjectName =
      examData.subjects[index]
        ?.subjectName;

    const confirmDelete =
      window.confirm(
        `Are you sure you want to remove "${subjectName}" from this datesheet?`
      );

    if (!confirmDelete) return;

    const updatedSubjects =
      examData.subjects.filter(
        (_, subjectIndex) =>
          subjectIndex !== index
      );

    setExamData({
      ...examData,
      subjects: updatedSubjects,
    });
  };

  // ======================================================
  // Save changes
  // ======================================================

  const handleSave = async () => {
    try {
      if (
        !examData.examName?.trim()
      ) {
        alert(
          "Please enter exam name."
        );
        return;
      }

      if (
        !examData.subjects ||
        examData.subjects.length === 0
      ) {
        alert(
          "At least one subject is required."
        );
        return;
      }

      // ==================================================
      // ONLY SAVE SUBJECTS WHICH HAVE SOME DATA
      // ==================================================

      const filledSubjects =
        examData.subjects.filter(
          (sub) =>
            sub.examDate ||
            sub.startTime ||
            sub.endTime ||
            sub.invigilator ||
            sub.room ||
            sub.notes
        );

      if (
        filledSubjects.length === 0
      ) {
        alert(
          "Please fill details for at least one subject."
        );
        return;
      }

      const sessionId =
        localStorage.getItem(
          "currentSessionId"
        );

      if (!sessionId) {
        alert(
          "Session ID is missing. Please log in again."
        );
        return;
      }

      // ==================================================
      // PREPARE PAYLOAD
      // ==================================================

      const payload = {
        examName:
          examData.examName,

        class:
          examData.class,

        sessionId,

        subjects:
          filledSubjects.map(
            (sub) => ({
              subjectName:
                sub.subjectName,

              examDate:
                sub.examDate
                  ? new Date(
                      sub.examDate
                    )
                  : undefined,

              startTime:
                sub.startTime || "",

              endTime:
                sub.endTime || "",

              invigilator:
                sub.invigilator ||
                "",

              notes:
                sub.notes || "",

              room:
                sub.room || "",
            })
          ),

        // Dynamic Co-Scholastic Areas
        coScholasticAreas:
          examData.coScholasticAreas ||
          [],
      };

      console.log(
        "📤 Update payload:",
        payload
      );

      // ==================================================
      // UPDATE EXAM
      // ==================================================

      const response =
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/exams/updateExam/${examData._id}`,
          payload,
          {
            params: {
              sessionId,
            },

            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "token"
              )}`,
            },
          }
        );

      console.log(
        "✅ Updated exam response:",
        response.data
      );

      // ==================================================
      // FORMAT RETURNED DATES
      // ==================================================

      const formattedSubjects = (
        response.data.subjects || []
      ).map((sub) => ({
        ...sub,

        examDate:
          sub.examDate
            ? new Date(
                sub.examDate
              )
                .toISOString()
                .split("T")[0]
            : "",
      }));

      // ==================================================
      // SET UPDATED DATA
      // ==================================================

      setExamData({
        ...response.data,

        subjects:
          formattedSubjects,

        coScholasticAreas:
          response.data
            .coScholasticAreas ||
          [],
      });

      setNewCoScholasticArea("");

      setIsEditing(false);

      alert(
        "Datesheet updated successfully"
      );

      navigate(
        "/exam-datesheet"
      );
    } catch (err) {
      console.error(
        "❌ Error updating datesheet:",
        err
      );

      console.error(
        "Backend error:",
        err.response?.data
      );

      alert(
        err.response?.data?.message ||
          "Failed to update datesheet"
      );
    }
  };

  // ======================================================
  // Cancel editing
  // ======================================================

  const handleCancel = async () => {
    try {
      const sessionId =
        localStorage.getItem(
          "currentSessionId"
        );

      if (!sessionId) {
        setIsEditing(false);
        return;
      }

      // ==================================================
      // RELOAD ORIGINAL DATA
      // ==================================================

      const response =
        await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/exams/getExam/${id}`,
          {
            params: {
              sessionId,
            },
          }
        );

      // ==================================================
      // RELOAD ALL CLASS SUBJECTS
      // ==================================================

      let allClassSubjects = [];

      try {
        const subjectResponse =
          await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${response.data.class}`,
            {
              params: {
                sessionId,
              },
            }
          );

        allClassSubjects =
          Array.isArray(
            subjectResponse.data
          )
            ? subjectResponse.data
            : [];
      } catch (subjectError) {
        console.error(
          "Error fetching class subjects while cancelling:",
          subjectError
        );
      }

      const existingSubjects =
        response.data.subjects || [];

      const existingSubjectMap =
        new Map();

      existingSubjects.forEach(
        (sub) => {
          existingSubjectMap.set(
            String(
              sub.subjectName || ""
            )
              .trim()
              .toLowerCase(),
            sub
          );
        }
      );

      const mergedSubjects =
        allClassSubjects.map(
          (subject) => {
            const key = String(
              subject.subjectName || ""
            )
              .trim()
              .toLowerCase();

            const existing =
              existingSubjectMap.get(
                key
              );

            if (existing) {
              return {
                ...existing,

                examDate:
                  existing.examDate
                    ? new Date(
                        existing.examDate
                      )
                        .toISOString()
                        .split("T")[0]
                    : "",
              };
            }

            return {
              subjectName:
                subject.subjectName,

              examDate: "",
              startTime: "",
              endTime: "",
              invigilator: "",
              notes: "",
              room: "",
            };
          }
        );

      // Keep old datesheet subjects
      existingSubjects.forEach(
        (existing) => {
          const key = String(
            existing.subjectName || ""
          )
            .trim()
            .toLowerCase();

          const alreadyIncluded =
            mergedSubjects.some(
              (subject) =>
                String(
                  subject.subjectName ||
                    ""
                )
                  .trim()
                  .toLowerCase() ===
                key
            );

          if (!alreadyIncluded) {
            mergedSubjects.push({
              ...existing,

              examDate:
                existing.examDate
                  ? new Date(
                      existing.examDate
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
            });
          }
        }
      );

      setExamData({
        ...response.data,

        subjects:
          mergedSubjects,

        coScholasticAreas:
          response.data
            .coScholasticAreas ||
          [],
      });

      setNewCoScholasticArea("");

      setIsEditing(false);
    } catch (err) {
      console.error(
        "Error restoring datesheet:",
        err
      );

      setIsEditing(false);
    }
  };

  // ======================================================
  // RETURN UI
  // ======================================================

  return (
    <div className="max-w-full mx-auto p-6 bg-white shadow-2xl rounded-lg mt-3">

      {/* ==================================================
          HEADER
      ================================================== */}

      <div className="flex justify-between items-center mb-6">

        <div className="flex-1">

          {isEditing ? (
            <div className="max-w-xl">

              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Exam Name
              </label>

              <input
                type="text"
                value={
                  examData.examName ||
                  ""
                }
                onChange={
                  handleExamNameChange
                }
                className="border border-gray-300 p-3 rounded-lg text-lg font-bold w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
              />

            </div>
          ) : (
            <h2 className="text-2xl font-bold text-center">
              Datesheet for{" "}
              {examData.class} -{" "}
              {examData.examName}
            </h2>
          )}

        </div>

        {/* ==================================================
            ACTION BUTTONS
        ================================================== */}

        <div className="ml-4">

          {isEditing ? (
            <div className="flex gap-2">

              <button
                onClick={
                  handleSave
                }
                className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg font-semibold"
              >
                Save
              </button>

              <button
                onClick={
                  handleCancel
                }
                className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded-lg font-semibold"
              >
                Cancel
              </button>

            </div>
          ) : (
            <button
              onClick={() =>
                setIsEditing(true)
              }
              className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold"
            >
              Edit
            </button>
          )}

        </div>
      </div>

      {/* ==================================================
          CO-SCHOLASTIC AREAS
      ================================================== */}

      <div className="mb-6 border border-gray-300 rounded-lg p-5 bg-gray-50">

        <div className="flex justify-between items-center mb-4">

          <h3 className="text-lg font-bold text-gray-800">
            Co-Scholastic Areas
          </h3>

          {!isEditing && (
            <span className="text-sm text-gray-500">
              {
                (
                  examData.coScholasticAreas ||
                  []
                ).length
              }{" "}
              Areas
            </span>
          )}

        </div>

        {/* ==================================================
            ADD AREA
        ================================================== */}

        {isEditing && (
          <div className="flex flex-col md:flex-row gap-3 mb-4">

            <input
              type="text"
              value={
                newCoScholasticArea
              }
              onChange={(e) =>
                setNewCoScholasticArea(
                  e.target.value
                )
              }
              onKeyDown={(e) => {
                if (
                  e.key === "Enter"
                ) {
                  e.preventDefault();

                  handleAddCoScholasticArea();
                }
              }}
              placeholder="Enter area name e.g. Discipline"
              className="border border-gray-300 rounded-lg px-3 py-2 flex-1"
            />

            <button
              type="button"
              onClick={
                handleAddCoScholasticArea
              }
              className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold"
            >
              + Add Area
            </button>

          </div>
        )}

        {/* ==================================================
            AREA LIST
        ================================================== */}

        {(
          examData.coScholasticAreas ||
          []
        ).length > 0 ? (

          <div className="flex flex-wrap gap-3">

            {(
              examData.coScholasticAreas ||
              []
            ).map(
              (area, index) => (
                <div
                  key={`${area.name}-${index}`}
                  className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-4 py-2"
                >

                  <span className="font-medium text-gray-800">
                    {area.name}
                  </span>

                  {isEditing && (
                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteCoScholasticArea(
                          index
                        )
                      }
                      className="text-red-500 hover:text-red-700 font-bold text-lg"
                      title="Remove area"
                    >
                      ×
                    </button>
                  )}

                </div>
              )
            )}

          </div>

        ) : (
          <p className="text-gray-500 text-sm">
            No Co-Scholastic Areas
            added.
            {isEditing &&
              " You can add one above."}
          </p>
        )}

      </div>

      {/* ==================================================
          DATE SHEET TABLE
      ================================================== */}

      <div className="overflow-x-auto">

        <table className="min-w-full border border-gray-300 divide-y divide-gray-200">

          <thead className="bg-gray-200">

            <tr>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Sr. No.
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Subject
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Date
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Timing
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Invigilator
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Room
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Notes
              </th>

              {isEditing && (
                <th className="border border-gray-300 px-4 py-3 text-center">
                  Action
                </th>
              )}

            </tr>

          </thead>

          <tbody className="bg-gray-100 divide-y divide-gray-200">

            {(examData.subjects || []).map(
              (sub, idx) => (

                <tr
                  key={
                    sub._id ||
                    `${sub.subjectName}-${idx}`
                  }
                  className="hover:bg-gray-50"
                >

                  {/* SR NO */}
                  <td className="border border-gray-300 px-4 py-3 text-center font-semibold">
                    {idx + 1}
                  </td>

                  {/* SUBJECT */}
                  <td className="border border-gray-300 px-4 py-3 text-center font-semibold">
                    {sub.subjectName}
                  </td>

                  {/* DATE */}
                  <td className="border border-gray-300 px-4 py-3 text-center">

                    {isEditing ? (
                      <input
                        type="date"
                        value={
                          sub.examDate ||
                          ""
                        }
                        onChange={(e) =>
                          handleChange(
                            idx,
                            "examDate",
                            e.target.value
                          )
                        }
                        className="border border-gray-300 rounded px-2 py-2"
                      />
                    ) : (
                      formatDateMMDDYYYY(
                        sub.examDate
                      )
                    )}

                  </td>

                  {/* TIMING */}
                  <td className="border border-gray-300 px-4 py-3 text-center">

                    {isEditing ? (

                      <div className="flex items-center justify-center gap-1">

                        <input
                          type="text"
                          value={
                            sub.startTime ||
                            ""
                          }
                          onChange={(e) =>
                            handleChange(
                              idx,
                              "startTime",
                              e.target.value
                            )
                          }
                          className="border border-gray-300 rounded px-2 py-2 w-24"
                          placeholder="Start"
                        />

                        <span>
                          -
                        </span>

                        <input
                          type="text"
                          value={
                            sub.endTime ||
                            ""
                          }
                          onChange={(e) =>
                            handleChange(
                              idx,
                              "endTime",
                              e.target.value
                            )
                          }
                          className="border border-gray-300 rounded px-2 py-2 w-24"
                          placeholder="End"
                        />

                      </div>

                    ) : (
                      `${sub.startTime || ""} - ${
                        sub.endTime || ""
                      }`
                    )}

                  </td>

                  {/* INVIGILATOR */}
                  <td className="border border-gray-300 px-4 py-3 text-center">

                    {isEditing ? (

                      <div className="min-w-[220px] text-left">

                        <Select
                          options={
                            teacherOptions
                          }
                          value={
                            teacherOptions.find(
                              (
                                option
                              ) =>
                                option.value ===
                                sub.invigilator
                            ) || null
                          }
                          onChange={(
                            selectedOption
                          ) =>
                            handleChange(
                              idx,
                              "invigilator",
                              selectedOption
                                ? selectedOption.value
                                : ""
                            )
                          }
                          placeholder="Select Invigilator"
                          isClearable
                          styles={{
                            control: (
                              base
                            ) => ({
                              ...base,
                              minHeight:
                                "42px",
                              fontSize:
                                "14px",
                            }),
                          }}
                        />

                      </div>

                    ) : (
                      sub.invigilatorName ||
                      teacherOptions.find(
                        (teacher) =>
                          teacher.value ===
                          sub.invigilator
                      )?.label ||
                      "-"
                    )}

                  </td>

                  {/* ROOM */}
                  <td className="border border-gray-300 px-4 py-3 text-center">

                    {isEditing ? (

                      <input
                        type="text"
                        value={
                          sub.room || ""
                        }
                        onChange={(e) =>
                          handleChange(
                            idx,
                            "room",
                            e.target.value
                          )
                        }
                        className="border border-gray-300 rounded px-2 py-2 w-28"
                        placeholder="Room"
                      />

                    ) : (
                      sub.room || "-"
                    )}

                  </td>

                  {/* NOTES */}
                  <td className="border border-gray-300 px-4 py-3 text-center">

                    {isEditing ? (

                      <input
                        type="text"
                        value={
                          sub.notes || ""
                        }
                        onChange={(e) =>
                          handleChange(
                            idx,
                            "notes",
                            e.target.value
                          )
                        }
                        className="border border-gray-300 rounded px-2 py-2 w-full min-w-[150px]"
                        placeholder="Notes"
                      />

                    ) : (
                      sub.notes || "-"
                    )}

                  </td>

                  {/* DELETE SUBJECT */}
                  {isEditing && (
                    <td className="border border-gray-300 px-4 py-3 text-center">

                      <button
                        type="button"
                        onClick={() =>
                          handleDeleteSubject(
                            idx
                          )
                        }
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg font-semibold"
                      >
                        Delete
                      </button>

                    </td>
                  )}

                </tr>

              )
            )}

          </tbody>

        </table>

      </div>

      {/* ==================================================
          NO SUBJECT MESSAGE
      ================================================== */}

      {(!examData.subjects ||
        examData.subjects.length === 0) && (
        <div className="text-center py-10 text-red-500 font-semibold">
          No subjects available in this datesheet.
        </div>
      )}

    </div>
  );
};

export default ViewDatesheet;
